import React, { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LogOut, User as UserIcon, ShieldCheck } from "lucide-react";

const links = [
  { to: "/", label: "HOME" },
  { to: "/about", label: "ABOUT" },
  { to: "/tests", label: "TESTS" },
  { to: "/archetypes", label: "ARCHETYPES" },
  { to: "/record-holders", label: "RECORDS" },
  { to: "/events", label: "EVENTS" },
  { to: "/leaderboard", label: "LEADERBOARD" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isVolunteer = !!user?.is_volunteer;

  return (
    <header className="sticky top-0 z-50" data-testid="navbar">
      <div className="gps-glass border-x-0 border-t-0 px-4 md:px-8 py-3 flex items-center justify-between" style={{ backdropFilter: "blur(22px)" }}>
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_gps-arena-1/artifacts/90b661035e6871a6_GPS%20Logo%20AI-13%20ggenerala.webp"
            alt="General Physicality Score"
            className="h-12 md:h-14 w-auto object-contain logo-glow"
            draggable={false}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `gps-heading text-[11px] tracking-[0.22em] px-3 py-2 transition-colors ${
                  isActive ? "text-cyan" : "text-mute hover:text-white"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" className="gps-btn ghost" data-testid="nav-admin">ADMIN</Link>
              )}
              {isVolunteer && (
                <Link to="/volunteer" className="gps-btn ghost" data-testid="nav-volunteer">
                  <ShieldCheck size={14} /> SCORING DECK
                </Link>
              )}
              <Link to="/my-gps" className="gps-btn" data-testid="nav-my-gps">
                <UserIcon size={14} /> MY GPS
              </Link>
              <button onClick={handleLogout} className="gps-btn ghost" data-testid="nav-logout">
                <LogOut size={14} /> LOGOUT
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="gps-btn ghost" data-testid="nav-login">LOGIN</Link>
              <Link to="/register" className="gps-btn" data-testid="nav-register">CALCULATE</Link>
            </>
          )}
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden gps-glass mx-3 mt-2 p-4 flex flex-col gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `gps-heading text-xs tracking-[0.22em] px-3 py-3 ${isActive ? "text-cyan" : "text-mute"}`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="hud-divider my-2" />
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setOpen(false)} className="gps-btn ghost" data-testid="nav-admin-mobile">ADMIN</Link>
              )}
              {isVolunteer && (
                <Link to="/volunteer" onClick={() => setOpen(false)} className="gps-btn ghost mt-2" data-testid="nav-volunteer-mobile">SCORING DECK</Link>
              )}
              <Link to="/my-gps" onClick={() => setOpen(false)} className="gps-btn mt-2">MY GPS</Link>
              <button onClick={() => { handleLogout(); setOpen(false); }} className="gps-btn ghost mt-2">LOGOUT</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="gps-btn ghost">LOGIN</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="gps-btn mt-2">CALCULATE</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
